import { SubmissionMethod, SubmissionType } from "encamp-prisma/generated/browser";

/**
 * The named RCRA waste "forms", each defined by the
 * `(submissionType, submissionMethod)` combos that constitute it.
 *
 * This is the single source of truth for "what is an 8700-12 / 8700-13." The
 * frontend stepper-eligibility check, the backend step config, the compliance
 * document-requirement rule, and the document-less review path all derive from
 * here — so the combos live in exactly one place.
 *
 * Forms split into two archetypes: interactive forms (8700-12 today) are
 * entered field-by-field in the stepper, while every other combo — named form
 * or not — is a document form: a paper/PDF filled out elsewhere and attached
 * as the submission document. Document forms share one capability catch-all
 * (`DOCUMENT_FORM_CAPABILITIES`).
 */
export enum WasteSubmissionForm {
  RCRA_8700_12 = "RCRA_8700_12",
  RCRA_8700_13 = "RCRA_8700_13",
}

type SubmissionCombo = { type: SubmissionType; method: SubmissionMethod };

const RCRA_8700_12_COMBO: SubmissionCombo = {
  type: SubmissionType.HAZARDOUS_WASTE_NOTIFICATION,
  method: SubmissionMethod.RCRAINFO_MYRCRAID_MODULE,
};

/** The defining fact: each form is the set of its `(type, method)` combos. */
const FORM_COMBOS: Record<WasteSubmissionForm, ReadonlyArray<SubmissionCombo>> = {
  [WasteSubmissionForm.RCRA_8700_12]: [RCRA_8700_12_COMBO],
  [WasteSubmissionForm.RCRA_8700_13]: [
    {
      type: SubmissionType.BIENNIAL_REPORT,
      method: SubmissionMethod.RCRAINFO_BIENNIAL_REPORTING_MODULE,
    },
    {
      type: SubmissionType.ANNUAL_REPORT,
      method: SubmissionMethod.RCRAINFO_ANNUAL_REPORTING_MODULE,
    },
  ],
};

export type FormCapabilities = {
  /** Entered through a guided stepper rather than the legacy upload flow. */
  stepperEligible: boolean;
  /** Reviewers approve the submission directly; there is no document to review. */
  documentlessReview: boolean;
  /** The compliance schema requires at least one submission document. */
  requiresSubmissionDocument: boolean;
};

/**
 * Human-readable form names, using the EPA form number plus the RCRAInfo-facing
 * description so users familiar with RCRAInfo can map our `(type, method)`
 * combos back to the form they know.
 */
const FORM_DISPLAY_NAME: Record<WasteSubmissionForm, string> = {
  [WasteSubmissionForm.RCRA_8700_12]: "EPA Form 8700-12 — Site Identification",
  [WasteSubmissionForm.RCRA_8700_13]: "EPA Form 8700-13 — Biennial/Annual Hazardous Waste Report",
};

/**
 * Capability entries for the interactive forms only — the forms entered
 * field-by-field in the stepper. Any combo without an entry here (including
 * named document forms like the 8700-13) is a document form and resolves to
 * `DOCUMENT_FORM_CAPABILITIES` instead.
 */
const INTERACTIVE_FORM_CAPABILITIES: Partial<Record<WasteSubmissionForm, FormCapabilities>> = {
  [WasteSubmissionForm.RCRA_8700_12]: {
    stepperEligible: true,
    documentlessReview: true,
    requiresSubmissionDocument: false,
  },
};

/**
 * The catch-all for document forms: paper/PDF forms filled out elsewhere and
 * attached as the submission document. They walk a guided (document) stepper,
 * always require the document, and keep the document-review path — reviewers
 * sign the attached document, so there is no document-less review.
 */
export const DOCUMENT_FORM_CAPABILITIES: FormCapabilities = {
  stepperEligible: true,
  documentlessReview: false,
  requiresSubmissionDocument: true,
};

/**
 * Resolves the waste form for a `(type, method)` combo, or `undefined` when the
 * combo is not one of our forms. Accepts plain strings so both the Prisma enums
 * (backend) and the GraphQL enums (frontend) can be passed without casting.
 */
export function getWasteSubmissionForm(
  submissionType?: string | null,
  submissionMethod?: string | null
): WasteSubmissionForm | undefined {
  if (!submissionType || !submissionMethod) {
    return undefined;
  }
  return (Object.keys(FORM_COMBOS) as WasteSubmissionForm[]).find((form) =>
    FORM_COMBOS[form].some(
      (combo) => combo.type === submissionType && combo.method === submissionMethod
    )
  );
}

/**
 * Human-readable name for the waste form a `(type, method)` combo resolves to,
 * or `undefined` when the combo is not one of our forms. Accepts plain strings
 * so both the Prisma enums (backend) and the GraphQL enums (frontend) work.
 */
export function getWasteSubmissionFormName(
  submissionType?: string | null,
  submissionMethod?: string | null
): string | undefined {
  const form = getWasteSubmissionForm(submissionType, submissionMethod);
  return form === undefined ? undefined : FORM_DISPLAY_NAME[form];
}

/**
 * The named form a type resolves to given only the methods on offer, so a row
 * that has lost its named method (multi-facility, disabled flow) stops
 * advertising that form. No type belongs to two forms, so first match wins.
 */
export function getWasteSubmissionFormNameForMethods(
  submissionType: string | null | undefined,
  submissionMethods: readonly string[]
): string | undefined {
  for (const submissionMethod of submissionMethods) {
    const formName = getWasteSubmissionFormName(submissionType, submissionMethod);
    if (formName !== undefined) {
      return formName;
    }
  }
  return undefined;
}

/**
 * The `(type, method)` combo created when a user opts into the stepper, so the
 * create-submission dialog can offer "fill out an 8700-12" as a single choice
 * instead of asking for the type and method that happen to map to it. Today the
 * 8700-12 is the only stepper-eligible form and it has exactly one combo;
 * revisit this shape if a multi-combo form ever becomes stepper-eligible.
 * Returned as plain strings so both the Prisma enums (backend) and the GraphQL
 * enums (frontend) can consume it without casting.
 */
export function getStepperSubmissionCombo(): { type: string; method: string } {
  return RCRA_8700_12_COMBO;
}

/**
 * Resolves the capabilities for a `(type, method)` combo: interactive forms
 * get their own entry, everything else — named document forms and unknown
 * combos alike — gets the document-form catch-all. Accepts plain strings so
 * both the Prisma enums (backend) and the GraphQL enums (frontend) work.
 */
export function getSubmissionFormCapabilities(
  submissionType?: string | null,
  submissionMethod?: string | null
): FormCapabilities {
  const form = getWasteSubmissionForm(submissionType, submissionMethod);
  const interactive = form === undefined ? undefined : INTERACTIVE_FORM_CAPABILITIES[form];
  return interactive ?? DOCUMENT_FORM_CAPABILITIES;
}

/** True when the combo is entered through the interactive (field-by-field) stepper. */
export function isStepperEligibleSubmission(
  submissionType?: string | null,
  submissionMethod?: string | null
): boolean {
  const form = getWasteSubmissionForm(submissionType, submissionMethod);
  return form !== undefined && (INTERACTIVE_FORM_CAPABILITIES[form]?.stepperEligible ?? false);
}

/**
 * True when the combo is a document form — any fully-specified combo without
 * an interactive stepper entry. A missing type or method is not a combo at
 * all, so it is neither archetype; callers dispatching on archetype should
 * treat that as "not a document form" (matching `isStepperEligibleSubmission`
 * returning false).
 */
export function isDocumentFormSubmission(
  submissionType?: string | null,
  submissionMethod?: string | null
): boolean {
  if (!submissionType || !submissionMethod) {
    return false;
  }
  return !isStepperEligibleSubmission(submissionType, submissionMethod);
}

/**
 * True when reviewers approve the submission directly (no document to review).
 *
 * `hasSubmissionSteps` is whether the submission has at least one
 * `SubmissionStep` row — the same marker the frontend uses to decide between
 * the stepper UI and the document-review UI (`usesStepper`). A combo match
 * alone is not enough: a legacy uploaded 8700-12 shares the combo but must
 * keep the document-review path so signing/document review is not bypassed.
 */
export function usesDocumentlessReview(
  submissionType: string | null | undefined,
  submissionMethod: string | null | undefined,
  hasSubmissionSteps: boolean
): boolean {
  const capabilities = getSubmissionFormCapabilities(submissionType, submissionMethod);
  return capabilities.documentlessReview && hasSubmissionSteps;
}

/**
 * True when the compliance schema should require a submission document. Only
 * stepper-created interactive forms are document-exempt: document forms always
 * require the attached document, and a legacy uploaded submission with an
 * interactive combo (no `SubmissionStep` rows) still requires its document.
 *
 * `hasSubmissionSteps` is whether the submission has at least one
 * `SubmissionStep` row (the stepper marker, mirroring the frontend's
 * `usesStepper`).
 */
export function requiresSubmissionDocument(
  submissionType: string | null | undefined,
  submissionMethod: string | null | undefined,
  hasSubmissionSteps: boolean
): boolean {
  const capabilities = getSubmissionFormCapabilities(submissionType, submissionMethod);
  return capabilities.requiresSubmissionDocument || !hasSubmissionSteps;
}
